/**
 * Public Landing contracts. These schemas contain only anonymous-safe projection
 * fields and strictly reject caller-defined analytics properties.
 */
import { z } from "zod";
import { canonicalIdSchema } from "./content.ts";
import {
  emptyWorkCardStats,
  workCardStatsSchema,
  workTicketBadgeSchema
} from "./home.ts";
import {
  emptyWorkSourceProvenance,
  sourceLanguageSchema,
  workSourceProvenanceSchema,
  workStatusSchema
} from "./work.ts";
import { workCoverSchema } from "./workCover.ts";
import { publicDetailHrefSchema } from "./publicUrls.ts";

const safeLabelSchema = z.string().trim().min(1);
const unicodeBoundedText = (maximumCodePoints: number) =>
  z.string().refine((value) => Array.from(value).length <= maximumCodePoints);

export const landingResponseSchema = z.object({
  product: z.object({
    headline: safeLabelSchema,
    subheadline: safeLabelSchema,
    registrationHref: z.string().startsWith("/")
  }),
  fandoms: z
    .array(
      z.object({
        id: canonicalIdSchema,
        name: safeLabelSchema,
        detailHref: publicDetailHrefSchema
      })
    )
    .max(18),
  ships: z
    .array(
      z.object({
        id: canonicalIdSchema,
        fandomId: canonicalIdSchema,
        fandomName: safeLabelSchema,
        label: safeLabelSchema,
        memberLabels: z.array(safeLabelSchema).min(2),
        ratingLabels: z.array(safeLabelSchema),
        warningLabels: z.array(safeLabelSchema),
        detailHref: publicDetailHrefSchema
      })
    )
    .max(12),
  contents: z
    .array(
      z.object({
        id: canonicalIdSchema,
        fandomId: canonicalIdSchema,
        shipId: canonicalIdSchema.nullable(),
        title: safeLabelSchema,
        summary: unicodeBoundedText(500),
        excerpt: unicodeBoundedText(300),
        creator: safeLabelSchema.optional(),
        creatorAvatarUrl: z
          .string()
          .trim()
          .min(1)
          .max(1_500_000)
          .nullable()
          .default(null),
        sourceLanguage: sourceLanguageSchema.optional(),
        status: workStatusSchema.optional(),
        tags: z.array(safeLabelSchema).optional(),
        ticketBadges: z.array(workTicketBadgeSchema).max(4).default([]),
        stats: workCardStatsSchema.default(emptyWorkCardStats),
        source: workSourceProvenanceSchema.default(emptyWorkSourceProvenance),
        cover: workCoverSchema.nullable().optional(),
        ratingLabels: z.array(safeLabelSchema),
        warningLabels: z.array(safeLabelSchema),
        detailHref: publicDetailHrefSchema,
        publishedAt: z.iso.datetime()
      })
    )
    .max(12)
});

export const anonymousLandingEventRequestSchema = z
  .object({
    eventId: canonicalIdSchema,
    eventName: z.enum([
      "landing_viewed",
      "landing_preview_opened",
      "registration_wall_viewed"
    ]),
    occurredAt: z.iso.datetime(),
    fandomId: canonicalIdSchema.optional(),
    shipId: canonicalIdSchema.optional(),
    contentId: canonicalIdSchema.optional(),
    properties: z.object({}).strict()
  })
  .strict();

export const anonymousLandingEventResponseSchema = z.object({
  accepted: z.literal(true)
});

export type LandingResponse = z.infer<typeof landingResponseSchema>;
export type AnonymousLandingEventRequest = z.infer<
  typeof anonymousLandingEventRequestSchema
>;
